import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DollarSign, Eye, Film, TrendingUp } from "lucide-react";

const resolve = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { if (user) api.get("/earnings/me").then((r) => setData(r.data)).catch(() => {}); }, [user]);

  if (!user) return <Navigate to="/login" />;

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <div className="mono text-xs text-primary">// creator dashboard</div>
      <h1 className="mt-2 text-5xl font-black tracking-tighter">Your earnings.</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">Verified experts earn on every view. Payouts are DEMO for now — actual Stripe payouts arrive with real audiences.</p>

      {!user.qualified ? (
        <div className="mt-10 border border-primary/40 bg-primary/5 p-8">
          <div className="text-lg font-bold">Verify to unlock earnings</div>
          <p className="mt-2 text-sm text-muted-foreground">Register as a Scientist or Content Creator with your credentials to publish videos and start earning per view.</p>
        </div>
      ) : (
        <>
          <div className="mt-10 grid gap-px bg-border sm:grid-cols-3">
            <Stat icon={DollarSign} k="Earnings (demo)" v={data ? `$${data.earnings_usd.toFixed(2)}` : "…"} />
            <Stat icon={Eye} k="Total views" v={data ? data.total_views : "…"} />
            <Stat icon={Film} k="Videos" v={data ? data.video_count : "…"} />
          </div>

          <div className="mt-10">
            <div className="mb-4 flex items-center gap-2 text-sm uppercase tracking-widest text-primary"><TrendingUp className="h-4 w-4" />Your videos</div>
            {data?.videos?.length ? (
              <div className="border border-border">
                {data.videos.map((v, i) => (
                  <div key={v.id} className={`flex items-center gap-4 p-4 ${i > 0 ? "border-t border-border" : ""}`}>
                    <div className="h-14 w-24 shrink-0 bg-muted overflow-hidden border border-border">
                      {v.thumbnail_url ? <img src={resolve(v.thumbnail_url)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-secondary to-muted" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold tracking-tight line-clamp-1">{v.title}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">{v.kind} · {new Date(v.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="mono text-primary">{v.views}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">views</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-border p-12 text-center text-muted-foreground">
                No videos yet — <Link to="/learn" className="text-primary hover:underline">publish your first</Link>.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, k, v }) => (
  <div className="bg-background p-6">
    <Icon className="h-5 w-5 text-primary" />
    <div className="mt-4 text-3xl font-black tracking-tighter">{v}</div>
    <div className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{k}</div>
  </div>
);
